import { uiText, useUiLanguage } from '../../../utils/uiTranslation';
import React, { useEffect, useState } from 'react';
import PreferencesSection from './PreferencesSection';
import SecuritySection from './SecuritySection';
import {
  getEnabledPermissionCount,
  getMemberInitials,
  summarizeDepartmentAccess
} from '../../../utils/teamAccess';

const AccountSection = ({
  accountEditing,
  accountForm,
  accountSaving,
  currentAccessProfile,
  currentUser,
  handleAccountEditToggle,
  handleAccountInputChange,
  handleAccountSave,
  handlePersonalPhotoChange,
  handlePersonalPhotoClick,
  handlePersonalPhotoRemove,
  handleTransferOwnership,
  isCompanyOwner,
  language,
  personalFileInputRef,
  personalPhotoUploading,
  personalPhotoRemoving,
  personalPhotoError,
  personalPhotoUrl,
  t
}) => {
  useUiLanguage();
  const [photoFailed, setPhotoFailed] = useState(false);
  // Removed or expired images must show initials, never the browser's broken-image text.
  useEffect(() => setPhotoFailed(false), [personalPhotoUrl]);
  const enabledPermissions = getEnabledPermissionCount(currentAccessProfile?.permissions);
  const departmentSummary = uiText(summarizeDepartmentAccess(currentAccessProfile, language));

  return (
    <div className="profile-section-stack">
      <div className="profile-company-grid settings-account-grid">
        <section className="profile-panel profile-panel-main">
          <div className="profile-panel-header">
            <div>
              <h2>{t('account.yourProfile', 'Your profile')}</h2>
              <p>{t('settings.personalDescription')}</p>
            </div>
            <button type="button" className="profile-btn-secondary" onClick={handleAccountEditToggle}>
              {accountEditing ? t('cancel', 'Cancel') : t('account.editName', 'Edit name')}
            </button>
          </div>

          <div className="profile-account-hero">
            <button type="button" className="profile-account-avatar profile-account-avatar--lg" aria-label={t('account.uploadPhoto')} disabled={personalPhotoUploading || personalPhotoRemoving} onClick={handlePersonalPhotoClick}>
              {personalPhotoUrl && !photoFailed ? (
                <img src={personalPhotoUrl} alt={currentUser?.displayName || currentUser?.email || uiText("User")} onError={() => setPhotoFailed(true)} />
              ) : (
                <span>{getMemberInitials({ name: currentUser?.displayName, email: currentUser?.email })}</span>
              )}
            </button>
            <div className="profile-account-copy">
              <strong>{currentUser?.displayName || currentAccessProfile?.name || currentUser?.email || uiText("User")}</strong>
              <div className="profile-account-role">
                {currentAccessProfile?.position || (isCompanyOwner ? t('admin', 'Admin') : t('collaborator', 'Collaborator'))}
              </div>
              <p>{currentUser?.email || t('account.noEmail', 'No email available')}</p>

              <div className="profile-account-meta">
                <div className="profile-account-meta-item">
                  <label>{t('account.departmentAccess', 'Department access')}</label>
                  <strong>{departmentSummary}</strong>
                </div>
                <div className="profile-account-meta-item">
                  <label>{t('account.permissionScope', 'Permission scope')}</label>
                  <strong>{t('account.enabledPermissions', '{{count}} enabled permissions', { count: enabledPermissions })}</strong>
                </div>
              </div>

              <div className="profile-account-actions">
                <button type="button" className="profile-inline-action" disabled={personalPhotoUploading || personalPhotoRemoving} onClick={handlePersonalPhotoClick}>
                  <i className="fa-regular fa-image" aria-hidden="true"></i>
                  {personalPhotoUploading ? t('account.uploadingPhoto', 'Uploading photo...') : t('account.uploadPhoto', 'Upload photo')}
                </button>
                {personalPhotoUrl && <button type="button" className="profile-inline-action profile-photo-remove" disabled={personalPhotoUploading || personalPhotoRemoving} onClick={handlePersonalPhotoRemove}>
                  <i className="fa-regular fa-trash-can" aria-hidden="true" />
                  {t(personalPhotoRemoving ? 'account.removingPhoto' : 'account.removePhoto')}
                </button>}
              </div>
              {personalPhotoError && <p className="settings-error" role="alert">{personalPhotoError}</p>}
            </div>
            <input
              ref={personalFileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePersonalPhotoChange}
              style={{ display: 'none' }}
            />
          </div>

          <div className="profile-field-grid">
            <div className="profile-field">
              <label htmlFor={accountEditing ? 'account-first-name' : undefined}>{t('account.firstName', 'First name')}</label>
              {accountEditing ? (
                <input
                  className="profile-input profile-input--dark"
                  type="text"
                  id="account-first-name" autoComplete="given-name"
                  value={accountForm.firstName}
                  onChange={(event) => handleAccountInputChange('firstName', event.target.value)}
                  placeholder={t('account.firstName', 'First name')}
                />
              ) : (
                <div className="profile-field-value">{currentAccessProfile?.firstName || accountForm.firstName || t('account.notSet', 'Not set')}</div>
              )}
            </div>

            <div className="profile-field">
              <label htmlFor={accountEditing ? 'account-last-name' : undefined}>{t('account.lastName', 'Last name')}</label>
              {accountEditing ? (
                <input
                  className="profile-input profile-input--dark"
                  type="text"
                  id="account-last-name" autoComplete="family-name"
                  value={accountForm.lastName}
                  onChange={(event) => handleAccountInputChange('lastName', event.target.value)}
                  placeholder={t('account.lastName', 'Last name')}
                />
              ) : (
                <div className="profile-field-value">{currentAccessProfile?.lastName || accountForm.lastName || t('account.notSet', 'Not set')}</div>
              )}
            </div>
          </div>

          {accountEditing && (
            <div className="profile-panel-actions">
              <button type="button" className="profile-btn-primary" onClick={handleAccountSave} disabled={accountSaving}>
                {accountSaving ? t('account.saving', 'Saving...') : t('account.saveName', 'Save name')}
              </button>
            </div>
          )}
        </section>

        <div className="profile-side-stack">
          <SecuritySection currentUser={currentUser} t={t} />
          <PreferencesSection />
          {isCompanyOwner && (
            <section className="profile-panel">
              <div className="profile-panel-header">
                <div>
                  <h2>{t('account.ownershipTitle', 'Ownership and access')}</h2>
                  <p>{t('account.ownershipDesc', 'Core workspace ownership changes are managed directly with the JOBSEEQR team.')}</p>
                </div>
              </div>
              <div className="profile-panel-actions profile-panel-actions--left">
                <button type="button" className="profile-btn-secondary" onClick={handleTransferOwnership}>
                  {t('account.requestTransfer', 'Request transfer')}
                </button>
              </div>
            </section>
          )}

          {!isCompanyOwner && (
            <section className="profile-panel">
              <div className="profile-panel-header">
                <div>
                  <h2>{t('account.accessSummaryTitle', 'Access summary')}</h2>
                  <p>{t('account.accessSummaryDesc', 'Your admin controls exactly which departments and features you can work with.')}</p>
                </div>
              </div>
              <div className="profile-mini-chip-row">
                <span className="profile-mini-chip">{departmentSummary}</span>
                <span className="profile-mini-chip">{t('account.enabledFeatures', '{{count}} enabled features', { count: enabledPermissions })}</span>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountSection;
